import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '../_services/user.service';
import { User } from '../_models/user';

@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.css']
})
export class OtpComponent implements OnInit, OnDestroy {

  otpForm: FormGroup;
  returnUrl: string;
  stateToken: string;
  countdown: number;
  attempts = 0;
  maxAttempts = 3;
  timerInterval: any;
  submitting = false;
  resending = false;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService
  ) { }

  ngOnInit() {
    this.stateToken = this.userService.getStateToken();
    if (!this.stateToken) {
      this.router.navigate(['/login']);
      return;
    }

    const expiry = this.userService.getStateTokenExpiry();
    const remaining = Math.floor((expiry - Date.now()) / 1000);
    if (remaining <= 0) {
      this.userService.clearStateToken();
      this.router.navigate(['/login']);
      return;
    }

    this.countdown = remaining;
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    this.otpForm = this.formBuilder.group({
      otp: ['', Validators.compose([Validators.required, Validators.pattern(/^\d{6}$/)])]
    });

    this.startTimer();
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  startTimer() {
    this.timerInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.timerInterval);
        this.userService.clearStateToken();
        this.toastr.error('OTP expired. Please login again.');
        this.router.navigate(['/login']);
      }
    }, 1000);
  }

  formatCountdown(): string {
    const minutes = Math.floor(this.countdown / 60);
    const seconds = this.countdown % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  onSubmit() {
    if (this.otpForm.invalid || this.submitting) {
      return;
    }

    this.submitting = true;
    const otpCode = this.otpForm.controls.otp.value;

    this.userService.verifyOtp(this.stateToken, otpCode).subscribe(
      (user: User) => {
        this.submitting = false;
        clearInterval(this.timerInterval);
        this.toastr.success('Logged in successfully');
        this.router.navigate([this.returnUrl]);
      },
      err => {
        this.submitting = false;
        this.attempts++;
        if (this.attempts >= this.maxAttempts) {
          this.userService.clearStateToken();
          clearInterval(this.timerInterval);
          this.toastr.error('Maximum OTP attempts exceeded. Please login again.');
          this.router.navigate(['/login']);
        } else {
          this.toastr.error(err.error.error || 'Invalid OTP code');
        }
      }
    );
  }

  resendOtp() {
    if (this.resending) {
      return;
    }

    this.resending = true;
    this.userService.resendOtp(this.stateToken).subscribe(
      response => {
        this.resending = false;
        this.stateToken = this.userService.getStateToken();
        const expiry = this.userService.getStateTokenExpiry();
        this.countdown = Math.floor((expiry - Date.now()) / 1000);
        this.toastr.success('OTP resent successfully');
      },
      err => {
        this.resending = false;
        this.toastr.error(err.error.error || 'Failed to resend OTP');
      }
    );
  }

  goToLogin() {
    this.userService.clearStateToken();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.router.navigate(['/login']);
  }
}
